import React, { useRef, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { API, Auth } from "aws-amplify";
import ReactTooltip from "react-tooltip";

function Chat() {
    const messagesRef = useRef();
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState({});
    const [anonymous, setAnonymous] = useState(false);
    const heightOfWidgets = 580;

    const queryClient = useQueryClient();
    const { data: usersOnline } = useQuery({
        queryKey: ["usersOnline"],
        queryFn: async () =>
            (await API.get("auth", "/cognito/usersOnline", {}))?.users,
        refetchInterval: 60000, //1 minute
    });

    const { data: messages } = useQuery({
        queryKey: ["chat"],
        queryFn: async () =>
            (
                await API.graphql({
                    query: `{
              chat(limit: 100, offset: 0, order_by: {created_at: asc}) {
                id
                created_at
                updated_at
                content
                username
                user_id
              }
            }`,
                })
            )?.data?.chat,
        refetchInterval: 1500, //1.5 seconds
    });

    useEffect(() => {
        const async = async () => {
            const currentUser = await Auth.currentAuthenticatedUser({
                bypassCache: false,
            });
            setUser(currentUser?.attributes);
            setLoading(false);
        };
        async();
        ReactTooltip.rebuild();
    }, []);

    useEffect(() => {
        messagesRef.current.scrollTo(0, 1000000);
    }, [messages?.length]);

    const onSubmit = async (e) => {
        console.log(user);
        e.preventDefault();

        const { input } = Object.fromEntries(new FormData(e.currentTarget));
        e.target.reset();

        await API.graphql({
            query: `mutation($content: String, $username: String) {
        insert_chat_one(object: {content: $content, username: $username}) {
          id
        }
      }`,
            variables: {
                content: input.slice(0, 500),
                username: anonymous
                    ? "Anonymous"
                    : `${user?.["custom:firstName"]} ${user?.["custom:lastName"]}`.slice(
                        0,
                        20,
                    ),
            },
        });

        await queryClient.refetchQueries(["chat"]);

        setTimeout(() => {
            messagesRef.current.scrollTo(0, 1000000);
        }, 0);
    };

    const URL_REGEX =
        /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;
    const renderText = (txt) =>
        txt.split(" ").map((part) =>
            URL_REGEX.test(part) ? (
                <a
                    target="_blank"
                    style={{ color: "#59af26", wordBreak: "break-word" }}
                    href={part}
                >
                    {part}{" "}
                </a>
            ) : (
                part + " "
            ),
        );

    return (
        <>
            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    margin: 'auto',
                    marginTop: 20,
                    width: 'calc(100% - 80px)',
                    transition: "opacity 1s",
                    opacity: loading ? 0 : 1,
                }}
            >
                <div
                    style={{
                        width: "100%",
                        height: heightOfWidgets,
                        border: "1px solid #2e2e2e",
                        borderRadius: 10,
                        color: "#fff",
                    }}
                >
                    <div
                        style={{
                            borderBottom: "1px solid #2e2e2e",
                            padding: 10,
                            height: 50,
                            fontSize: 24,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: 10,
                            }}
                        >
                            <div
                                style={{
                                    color: "#fff",
                                    fontFamily:
                                        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                                    fontWeight: 700,
                                }}
                            >
                                Chat
                            </div>

                            <div
                                data-tip={`Welcome to Longbow Chat, a space where you can message other users. Your full name serves as your username (as set in your profile). Messages are capped at 500 characters. Only the most recent 100 messages are loaded.`}
                                style={{
                                    border: "1px solid #999",
                                    borderRadius: "100%",
                                    width: 20,
                                    height: 20,
                                    textAlign: "center",
                                    fontSize: 13,
                                    color: "#999",
                                    cursor: "pointer",
                                }}
                            >
                                ?
                            </div>
                        </div>
                        <div
                            style={{
                                fontSize: 12,
                                color: "#999",
                            }}
                        >
                            {user?.profile === "admin" && (
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "center",
                                        justifyContent: "flex-end",
                                        gap: 5,
                                    }}
                                >
                                    <div>{usersOnline ?? 0} users online</div>
                                    <div
                                        style={{
                                            height: 13,
                                            width: 13,
                                            backgroundColor: "#59af26",
                                            borderRadius: "100%",
                                            marginTop: -2,
                                        }}
                                    />
                                </div>
                            )}
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "flex-start",
                                    gap: 5,
                                    cursor: "pointer",
                                }}
                                onClick={() => {
                                    setAnonymous(!anonymous);
                                }}
                            >
                                <div style={{ marginTop: 2 }}>Chat anonymously</div>
                                <input checked={anonymous} type="checkbox" />
                            </div>
                        </div>
                    </div>
                    <div
                        style={{
                            width: "100%",
                            height: 628,
                            overflowY: "scroll",
                            overflowX: "clip",
                            width: "calc(100% + 10px)",
                        }}
                    >
                        <div
                            ref={messagesRef}
                            style={{
                                height: "calc(100% - 150px)",
                                borderRadius: 5,
                                overflowY: "scroll",
                                color: "#fff",
                                padding: 8,
                                fontSize: 14,
                            }}
                        >
                            {messages?.map((obj) => (
                                <div style={{ margin: "12px 0px" }}>
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "row",
                                            justifyContent: "space-between",
                                            fontSize: 11,
                                            margin: "0px 5px",
                                        }}
                                    >
                                        <div>{`${obj?.username}`}</div>
                                        <div style={{ color: "#999" }}>
                                            {new Date(obj?.created_at)
                                                ?.toLocaleString("en-US", {
                                                    month: "numeric",
                                                    day: "numeric",
                                                    hour: "numeric",
                                                    minute: "numeric",
                                                })
                                                ?.replace(" AM", "am")
                                                ?.replace(" PM", "pm")}
                                        </div>
                                    </div>
                                    <div
                                        style={{
                                            backgroundColor:
                                                obj?.user_id !== user?.sub ? "#444" : "#4c80f2",
                                            padding: "2px 6px",
                                            borderRadius: 8,
                                            whiteSpace: "break-spaces",
                                        }}
                                    >
                                        {renderText(obj.content)}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <form onSubmit={onSubmit}>
                            <input
                                disabled={loading}
                                name="input"
                                placeholder="Type a message..."
                                style={{
                                    height: 50,
                                    fontSize: 14,
                                    backgroundColor: "#000",
                                    color: "#fff",
                                    padding: 10,
                                    width: "100%",
                                    border: "1px solid #ccc",
                                    borderRadius: 5,
                                }}
                            />
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
}
export default React.memo(Chat);
